import { ActionDefinition } from "components/actions/actions";
import { OptionHolder } from "components/input/select/options";
import { RestrictionHolder } from "components/restrictions/Restrictions";
import { Bundle } from "../Bundle";
import { EventActionDefinition } from "../events/eventActions";

export interface ParentComponent extends PostRenderedComponent {
    children: Component[];
    appendChildView(child: Node): void;
}

export interface PostRenderedComponent extends Component {
    postRender(): void;
}

export interface BindableComponent extends Component {
    definition: BindableComponentDefinition;
}

export interface ValueComponent extends BindableComponent {
    getValue(): any;
    value(data: any): void;
    definition: ValueComponentDefinition;
}

export interface InputComponent extends ValueComponent, DisableableComponent {
    definition: ValueComponentDefinition;
    setRequired(required: boolean): void;
}

export interface RestrictableComponent extends Component {
    applyRestrictions(restricionts: RestrictionHolder): void;
}

export interface ValidationStatusComponent extends RestrictableComponent {
    setValidationStatus(valid: boolean, message?: string): void;
}

export interface DisplayedComponent extends Component {
    view: HTMLElement;
}

/**
 * A component that holds a full object that can be used in actions.
 * Ex: form and table.
 */
export interface DataHolderComponent extends Component {
    selectDataForAction(action: ActionDefinition): any;
    updateData(data: any): void;
    setCommittedStatus(): void;
}
export interface DataAppendableComponent extends DataHolderComponent {
    appendData(data: any): void;
}
export function isDataAppendableComponent(arg: any): arg is DataAppendableComponent {
    return arg.appendData !== undefined && typeof arg.appendData === "function";
}
export interface AutoValueComponent extends Component {
    autoInitValue(): void;
}
export interface NeedsUnhideComponent extends Component {
    unhide(): void;
}
export interface ParentAwareComponent extends Component {
    parent: Component;
}

export interface DisableableComponent extends Component {
    setReadOnly(ro: boolean): void;
}

/**
 * A component that can hold validation rules.
 * Ex. a form or a table row.
 */
export interface ValidatorComponent {
    setRequired(property: string, required: boolean): void;
    initRestrictions(restrictions: RestrictionHolder): void;
}

export interface ComponentWithOptions extends ValueComponent {
    addOptions(options: OptionHolder): void;
    setOptions(options: OptionHolder): void;
}
export interface ComponentThatCanDisableOptions extends ValueComponent {
    enableOptions(options: Array<string | number>): void;
    disableOptions(options: Array<string | number>): void;
}


export interface Component extends Renderable {
    /**
     * A name that uniquely identifies the type of the component.
     */
    componentType: string;
    /**
     * A name that uniquely identifies the component.
     */
    id: string;
    definition: ComponentDefinition;
}

export interface ActionComponent extends Component {
    definition: ActionComponentDefinition;
}

export interface ActionComponentDefinition extends ComponentDefinition {
    eventActions: EventActionDefinition[];
}

export interface FocusableComponent extends Component {
    focus: () => void;
}
export interface HideableComponent extends Component {
    show: () => void;
    hide: () => void;
}

export interface Renderable {
    render(): Element;
}

/**
 * A component that hosts components.
 */
export interface NestedComponentContainer {
    getNestedComponents(): Component[];
}

export function isNestedComponentContainer(arg: any): arg is NestedComponentContainer {
    return arg != null && arg.getNestedComponents !== undefined;
}

export interface ComponentDefinition {

    /**
     * A name that uniquely identifies the type of the component.
     */
    componentType: string;
    /**
     * A name that uniquely identifies the component.
     */
    id: string;
    width?: number;

    children?: Array<ComponentDefinition | Bundle>;
}

export interface LabeledComponentDefinition extends ComponentDefinition {
    labelKey: string;
    labelLongKey?: string;
    labelWidth?: number;
}
export interface SingleInputComponentDefinition extends ComponentDefinition {
    inputWidth?: number;
}
export interface EmbedableComponentDefinition extends ComponentDefinition {
    host: string;
    container: string;
}

export interface ValueComponentDefinition extends BindableComponentDefinition {
    property: string;
}

export interface BindableComponentDefinition extends ComponentDefinition {
    property?: string;
}

export function isComponent(arg: any): arg is Component {
    return arg != null && arg.componentType !== undefined;
}

export function isLabeledComponentDefinition(arg: any): arg is LabeledComponentDefinition {
    return arg != null && arg.labelKey !== undefined;
}

export function isAutoValueComponent(arg: any): arg is AutoValueComponent {
    return arg != null && arg.autoInitValue !== undefined && isComponent(arg);
}
export function isParentComponent(arg: any): arg is ParentComponent {
    return arg != null && arg.children !== undefined && isComponent(arg);
}
export function isValueComponent(arg: any): arg is ValueComponent {
    return arg != null && arg.value !== undefined;
}
export function isInputComponent(arg: any): arg is InputComponent {
    return arg != null && arg.setRequired !== undefined && isDisableableComponent(arg);
}
export function isDisableableComponent(arg: any): arg is DisableableComponent {
    return arg != null && arg.setReadOnly !== undefined;
}
export function isPostRenderedComponent(arg: any): arg is PostRenderedComponent {
    return arg != null && arg.postRender !== undefined;
}
export function isRestrictableComponent(arg: any): arg is RestrictableComponent {
    return arg != null && arg.applyRestrictions !== undefined;
}
export function isValidationStatusComponent(arg: any): arg is ValidationStatusComponent {
    return arg != null && arg.applyRestrictions !== undefined && arg.setValidationStatus !== undefined;
}
export function isParentAwareComponent(arg: any): arg is ParentAwareComponent {
    return arg != null && arg.parent !== undefined;
}
export function isNeedsUnhideComponent(arg: any): arg is NeedsUnhideComponent {
    return arg != null && arg.unhide !== undefined;
}
export function isComponentWithOptions(arg: any): arg is ComponentWithOptions {
    return arg != null && arg.addOptions !== undefined && isValueComponent(arg);
}
export function isComponentThatCanDisableOptions(arg: any): arg is ComponentThatCanDisableOptions {
    return arg != null && arg.disableOptions !== undefined && isValueComponent(arg);
}

export function isFocusableComponent(arg: any): arg is FocusableComponent {
    return arg != null && arg.focus !== undefined;
}

export function isDisplayedComponent(arg: any): arg is DisplayedComponent {
    return arg != null && arg.view !== undefined;
}

export function isDataHolderComponent(arg: any): arg is DataHolderComponent {
    return arg != null
        && arg.selectDataForAction !== undefined
        && arg.updateData !== undefined
        && arg.setCommittedStatus !== undefined;
}

export function isHideableComponent(arg: any): arg is HideableComponent {
    return arg != null && arg.hide !== undefined && arg.show !== undefined;
}
